import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Toast from './Toast';
import Modal from './common/Modal';
import useToast from '../hooks/useToast';
import { fetchReleasedWorkOrders, fetchProductionEmployees, fetchDefectCodes, getShelfCountSetting, submitShelfCount, submitProductionReport, getProductionHistory, getSavedWorkOrders, getReportedWorkOrders, subscribeToWorkOrders } from '../supabase/services';
import { RefreshCw, Send, Printer, ClipboardList, History, X, Filter, Layers, Search, Palette, AlertTriangle, Info } from 'lucide-react';
import './ReportProduction.css';
import {
  ROUTING_DISPLAY_NAMES,
  makeBatchKey,
  sumItems,
  sumReported,
  sumRemaining,
  remainingFor,
  progressPct,
  dueDateKey,
  mergeQuantities,
  remainingBySerial,
  overPlanBy,
  needsOverPlanConfirm,
  exceedsReportLimit,
  formatAge,
  asksShelfCount,
  stockPartsFor,
  colourGroupFor,
  comparePartNames
} from './production/productionUtils';
import { describeLineFailure } from './production/reportErrors';
import { buildLabelsDocument } from './production/labelPrinting';
import { buildCuttingListDocument } from './production/cuttingListPrinting';
import { buildWorkOrdersDocument } from './production/workOrdersPrinting';
import WorkOrdersTable from './production/WorkOrdersTable';
import WorkOrdersCards from './production/WorkOrdersCards';
import ProductionHistory from './production/ProductionHistory';
import CuttingList from './production/CuttingList';
import usePartBalances from './production/usePartBalances';
import DueDate from './common/DueDate';
import Meter from './common/Meter';
import { ConfirmReportModal, LabelPickerModal, ReportResultModal } from './production/ProductionModals';

// Selection semantics:
//   - A selected work order that disappears from the list (fully reported by someone
//     else, realtime removal, refetch) is dropped from the selection immediately.
//   - Selections are NOT cleared when the operator changes the routing/customer filter or
//     search, so they can build a batch across filters. Those hidden selections are still
//     submitted, and the confirm dialog lists every one of them and flags the hidden ones,
//     so what is submitted is always exactly what the dialog shows.
//   - When realtime removes a selected work order (someone else completed it) an info
//     toast names it, and rows whose quantities changed under the operator flash briefly.

// localStorage read/write that never throws (private mode, blocked storage)
const readSetting = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeSetting = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
  } catch { /* preference only */ }
};

const NO_PARTS = [];

// Our own submission's realtime echo is not "someone else" for this long after it ends
const OWN_ECHO_MS = 30000;
// How long a row stays highlighted after a realtime quantity change
const CHANGE_FLASH_MS = 3000;

const ReportProduction = () => {
  // View mode: 'workOrders' for main view, 'history' for past submissions
  const [viewMode, setViewMode] = useState('workOrders');

  // Work orders from Priority
  const [workOrders, setWorkOrders] = useState([]);
  // Start true so the skeleton shows on first paint until the initial subscription load resolves.
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  // Ticks every minute so "Updated N min ago" stays current
  const [now, setNow] = useState(() => new Date());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // serial -> "short of components" flag from the last Priority fetch. The flag isn't
  // stored in the database, so realtime refetches would otherwise drop it.
  const [missingComponents, setMissingComponents] = useState({});

  // Selected items and quantities
  const [selectedItems, setSelectedItems] = useState(new Set());
  // Latest selection for the realtime callback, which is subscribed once
  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;
  // Serials in our latest submission and when its realtime echo stops being expected
  const ownSubmissionRef = useRef({ serials: new Set(), until: 0 });
  // Serials whose quantities just changed via realtime (highlighted briefly)
  const [changedSerials, setChangedSerials] = useState(() => new Set());
  const changeTimerRef = useRef(null);
  const [quantities, setQuantities] = useState({}); // serialName -> quantity
  // Serials whose "To Report" quantity the user edited by hand. Untouched rows follow the
  // latest remaining quantity when the list refreshes (see mergeQuantities).
  const touchedQuantitiesRef = useRef(new Set());
  // serial -> remaining quantity from the previous list, to detect drops on refresh
  const prevRemainingRef = useRef({});
  const [rejectQuantities, setRejectQuantities] = useState({}); // serialName -> reject quantity
  const [defectCodes, setDefectCodes] = useState([]); // Priority DEFECTCODES, empty if unavailable
  const [defectCodeSelections, setDefectCodeSelections] = useState({}); // serialName -> defect code
  // { key, signature } for the batch awaiting/undergoing submission. Retained only while an
  // attempt's outcome is unknown, and only reused for an identical selection.
  const [pendingBatch, setPendingBatch] = useState(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Outcome dialog: { kind: 'success' | 'partial' | 'failed', reported, failed, warnings, ... }
  const [reportResult, setReportResult] = useState(null);

  // Label printing: { items, lineResults, date } waiting for the layout picker
  const [pendingPrintData, setPendingPrintData] = useState(null);

  // History state
  const [historyData, setHistoryData] = useState([]);
  const [reportedWorkOrders, setReportedWorkOrders] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [historyViewType, setHistoryViewType] = useState('byReport'); // 'byReport' | 'byWorkOrder'

  // Activity filter state - persist to localStorage
  const [activityFilter, setActivityFilter] = useState(() => readSetting('productionActivityFilter') || 'all');

  // Employee picker state. The remembered employee is only a suggestion: the confirm
  // dialog makes them tap "I'm <name>" each time (see EmployeePicker).
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(() => {
    const saved = readSetting('productionEmployeeId');
    return saved ? Number(saved) : '';
  });

  // Customer filter
  const [customerFilter, setCustomerFilter] = useState('all');

  // Quick search across part description, SKU, work order and sales order
  const [searchQuery, setSearchQuery] = useState('');

  // Group same-SKU work orders together - persist to localStorage
  const [groupBySku, setGroupBySku] = useState(() => readSetting('productionGroupBySku') === 'true');

  // Group by colour (on by default) so staff make - and the printed sheet lists - one colour at a time
  const [groupByColour, setGroupByColour] = useState(() => readSetting('productionGroupByColour') !== 'false');

  // Sort state
  const [sortField, setSortField] = useState('dueDate'); // 'workOrder', 'partDescription', 'customer', 'dueDate', 'planned'
  const [sortDirection, setSortDirection] = useState('asc');

  // Toasts. This view's calls default to 'success' (the shared hook defaults to 'info').
  const { toastProps, showToast: pushToast } = useToast();
  const showToast = useCallback((message, type = 'success') => pushToast(message, type), [pushToast]);


  // serial -> work order, for lookups instead of repeated .find()
  const workOrdersBySerial = useMemo(
    () => new Map(workOrders.map(wo => [wo.serialName, wo])),
    [workOrders]
  );

  // Get unique routing types from work orders (using processName = PROCNAME from Priority)
  // and the planned item total for each tab.
  const { activityTypes, activityCounts } = useMemo(() => {
    const counts = new Map();
    workOrders.forEach(wo => {
      if (!wo.processName) return;
      counts.set(wo.processName, (counts.get(wo.processName) || 0) + (Number(wo.quantity) || 0));
    });
    return { activityTypes: [...counts.keys()], activityCounts: counts };
  }, [workOrders]);

  // The saved activity only applies while it's one of several activities actually present.
  // Otherwise fall back to 'all' - the tabs are hidden with fewer than two activities, so a
  // stale saved filter (e.g. from another day's work orders) would leave no way to clear it.
  const effectiveActivity = activityFilter !== 'all'
    && activityTypes.length > 1
    && activityTypes.includes(activityFilter)
    ? activityFilter
    : 'all';

  // Work orders after the activity filter
  const activityFiltered = useMemo(() => (
    effectiveActivity === 'all'
      ? workOrders
      : workOrders.filter(wo => wo.processName === effectiveActivity)
  ), [workOrders, effectiveActivity]);

  // Shelf count when reporting frames / mounts (app-wide switch, on the Settings page). While
  // it's on, Priority's stock is hidden so the count is a real count, not a copy.
  const [shelfCountEnabled, setShelfCountEnabled] = useState(true);
  // Parts whose first count was too far off to post: part -> first count
  const [shelfRecount, setShelfRecount] = useState({});

  // Priority stock for the frame / mount parts in view, so the shelf can be checked
  // against it before reporting. Not loaded while the shelf count is on.
  const stockPartsInView = useMemo(() => stockPartsFor(activityFiltered), [activityFiltered]);
  const stockParts = shelfCountEnabled ? NO_PARTS : stockPartsInView;
  const {
    balances: partBalances,
    loading: stockLoading,
    fetchedAt: stockFetchedAt,
    failed: stockFailed,
    refresh: refreshStock
  } = usePartBalances(stockParts);

  // Unique customers (after activity filter) with planned item totals
  const { customerNames, customerCounts } = useMemo(() => {
    const counts = new Map();
    activityFiltered.forEach(wo => {
      const name = wo.customerName || wo.customerCode;
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + (Number(wo.quantity) || 0));
    });
    return { customerNames: [...counts.keys()].sort(), customerCounts: counts };
  }, [activityFiltered]);

  const searchTerm = searchQuery.trim().toLowerCase();

  // Filter by customer + quick search, then sort
  const filteredWorkOrders = useMemo(() => {
    const customerFiltered = customerFilter === 'all'
      ? activityFiltered
      : activityFiltered.filter(wo => (wo.customerName || wo.customerCode) === customerFilter);

    const filteredUnsorted = searchTerm
      ? customerFiltered.filter(wo =>
          [wo.partDescription, wo.partName, wo.serialName, wo.salesOrder, wo.customerName, wo.customerCode]
            .some(field => field && String(field).toLowerCase().includes(searchTerm)))
      : customerFiltered;

    // Colour label per colour key (first description seen), so groups sort by name
    const colourLabels = new Map();
    if (groupByColour) {
      filteredUnsorted.forEach(wo => {
        const { key, label } = colourGroupFor(wo);
        if (!colourLabels.has(key)) colourLabels.set(key, label);
      });
    }

    return [...filteredUnsorted].sort((a, b) => {
      // Colour first, then size (part code), then the chosen column
      if (groupByColour) {
        const ka = colourGroupFor(a).key;
        const kb = colourGroupFor(b).key;
        if (ka !== kb) {
          return (colourLabels.get(ka) || ka).localeCompare(colourLabels.get(kb) || kb, 'en', { sensitivity: 'base' })
            || ka.localeCompare(kb);
        }
        const bySize = comparePartNames(a.partName, b.partName);
        if (bySize !== 0) return bySize;
      }
      let cmp = 0;
      switch (sortField) {
        case 'workOrder':
          cmp = (a.serialName || '').localeCompare(b.serialName || '');
          break;
        case 'partDescription':
          cmp = (a.partDescription || a.partName || '').localeCompare(b.partDescription || b.partName || '');
          break;
        case 'customer':
          cmp = (a.customerName || a.customerCode || '').localeCompare(b.customerName || b.customerCode || '');
          break;
        case 'dueDate': {
          // Guard against unparseable dates (getTime() -> NaN) which would make the sort
          // unstable; treat invalid/missing dates as the far future so they sort last.
          const ta = new Date(a.endDate || a.plannedDate || '9999-12-31').getTime();
          const tb = new Date(b.endDate || b.plannedDate || '9999-12-31').getTime();
          cmp = (isNaN(ta) ? Infinity : ta) - (isNaN(tb) ? Infinity : tb);
          break;
        }
        case 'planned':
          cmp = (a.quantity || 0) - (b.quantity || 0);
          break;
        case 'remaining':
          cmp = remainingFor(a) - remainingFor(b);
          break;
        default:
          cmp = 0;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [activityFiltered, customerFilter, searchTerm, sortField, sortDirection, groupByColour]);

  // Build display rows. When grouping by SKU, work orders sharing the same part
  // are pulled together (at the position of their first occurrence in the current
  // sort order) and SKUs with 2+ work orders get a group header row.
  // When grouping by colour, each colour run (already contiguous from the sort) gets a
  // colour header row and SKU grouping applies within it.
  const displayRows = useMemo(() => {
    const rows = [];
    const pushSkuRows = (list) => {
      if (!groupBySku) {
        list.forEach(wo => rows.push({ type: 'wo', wo, grouped: false }));
        return;
      }
      const groups = new Map();
      list.forEach(wo => {
        const key = wo.partName || wo.partDescription || wo.serialName;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(wo);
      });
      groups.forEach((wos, key) => {
        if (wos.length > 1) {
          rows.push({ type: 'group', key, wos });
          wos.forEach(wo => rows.push({ type: 'wo', wo, grouped: true }));
        } else {
          rows.push({ type: 'wo', wo: wos[0], grouped: false });
        }
      });
    };

    if (groupByColour) {
      const colours = [];
      filteredWorkOrders.forEach(wo => {
        const { key, label } = colourGroupFor(wo);
        const last = colours[colours.length - 1];
        if (last && last.key === key) last.wos.push(wo);
        else colours.push({ key, label, wos: [wo] });
      });
      colours.forEach(({ key, label, wos }) => {
        rows.push({ type: 'colour', key, label, wos });
        pushSkuRows(wos);
      });
    } else {
      pushSkuRows(filteredWorkOrders);
    }
    return rows;
  }, [filteredWorkOrders, groupBySku, groupByColour]);

  // When every visible work order shares a customer/order or due date, show it once
  // above the table instead of repeating it on every row.
  const { hideCustomerCol, hideDateCol } = useMemo(() => {
    const uniqueValues = (fn) => [...new Set(filteredWorkOrders.map(fn))];
    const sharedCustomers = uniqueValues(wo => `${wo.customerName || wo.customerCode || ''}|${wo.salesOrder || ''}`);
    const sharedDates = uniqueValues(wo => dueDateKey(wo.endDate || wo.plannedDate));
    return {
      hideCustomerCol: filteredWorkOrders.length > 1 && sharedCustomers.length === 1 && sharedCustomers[0] !== '|',
      hideDateCol: filteredWorkOrders.length > 1 && sharedDates.length === 1 && sharedDates[0] !== ''
    };
  }, [filteredWorkOrders]);
  const sharedWo = filteredWorkOrders[0];

  // Work orders older than 30 minutes may be out of date
  const isStale = Boolean(lastFetchTime) && (now - lastFetchTime) > 30 * 60000;

  const summaryTitle = effectiveActivity === 'all'
    ? (activityTypes.length === 1 ? (ROUTING_DISPLAY_NAMES[activityTypes[0]] || activityTypes[0]) : 'All work orders')
    : (ROUTING_DISPLAY_NAMES[effectiveActivity] || effectiveActivity);
  const summaryPlanned = sumItems(filteredWorkOrders);
  const summaryReported = sumReported(filteredWorkOrders);
  const summaryRemaining = sumRemaining(filteredWorkOrders);
  const summaryPct = progressPct(summaryReported, summaryPlanned);

  const isShort = useCallback(
    (wo) => Boolean(wo.missingComponents ?? missingComponents[wo.serialName]),
    [missingComponents]
  );

  // Select/deselect every work order in a SKU group
  const handleSelectGroup = (wos) => {
    const serials = wos.map(w => w.serialName);
    const allSelected = serials.every(s => selectedItems.has(s));
    setSelectedItems(prev => {
      const next = new Set(prev);
      serials.forEach(s => (allSelected ? next.delete(s) : next.add(s)));
      return next;
    });
  };

  // Handle sort toggle
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Highlight rows whose quantities just changed under the operator
  const flashChanged = useCallback((serials) => {
    setChangedSerials(new Set(serials));
    clearTimeout(changeTimerRef.current);
    changeTimerRef.current = setTimeout(() => setChangedSerials(new Set()), CHANGE_FLASH_MS);
  }, []);

  useEffect(() => () => clearTimeout(changeTimerRef.current), []);

  // Replace the work order list and bring "To Report" defaults in line with it. `live`
  // marks a realtime update (someone else's change), which is announced: selected work
  // orders that vanished get a toast, rows with new quantities flash. Our own
  // submission's echo is skipped.
  const applyWorkOrdersList = useCallback((workOrdersList, { live = false } = {}) => {
    const prevRemaining = prevRemainingRef.current;
    const touched = touchedQuantitiesRef.current;
    prevRemainingRef.current = remainingBySerial(workOrdersList);
    setWorkOrders(workOrdersList);
    setQuantities(prev => mergeQuantities(prev, workOrdersList, touched, prevRemaining));

    if (!live) return;
    const own = ownSubmissionRef.current;
    const isOwn = (serial) => Date.now() < own.until && own.serials.has(serial);
    const present = new Set(workOrdersList.map(wo => wo.serialName));
    const vanished = [...selectedItemsRef.current].filter(serial => !present.has(serial) && !isOwn(serial));
    if (vanished.length > 0) {
      showToast(
        `${vanished.join(', ')} ${vanished.length === 1 ? 'was' : 'were'} completed or closed elsewhere and removed from your selection`,
        'info'
      );
    }
    const changed = workOrdersList
      .filter(wo => prevRemaining[wo.serialName] !== undefined
        && prevRemaining[wo.serialName] !== remainingFor(wo)
        && !isOwn(wo.serialName))
      .map(wo => wo.serialName);
    if (changed.length > 0) flashChanged(changed);
  }, [showToast, flashChanged]);

  // Load saved work orders from DB on mount (no auto-fetch from Priority)
  // Subscribe to Realtime for live updates when webhook fires
  useEffect(() => {
    // Subscribe to Realtime for live updates from webhook
    const unsubscribe = subscribeToWorkOrders((workOrdersList) => {
      applyWorkOrdersList(workOrdersList, { live: true });
      setIsLoading(false);
      setIsInitialLoad(false);
      if (workOrdersList.length > 0 && workOrdersList[0]?.fetchedAt) {
        const fetchedAt = new Date(workOrdersList[0].fetchedAt);
        setLastFetchTime(fetchedAt);
      }
    }, () => {
      // Stop the skeleton and keep whatever was already on screen
      setIsLoading(false);
      setIsInitialLoad(false);
      showToast('Could not load work orders. Check your connection and try again.', 'error');
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [applyWorkOrdersList, showToast]);

  // Drop selections for work orders that no longer exist
  useEffect(() => {
    setSelectedItems(prev => {
      const next = new Set([...prev].filter(serial => workOrdersBySerial.has(serial)));
      return next.size === prev.size ? prev : next;
    });
  }, [workOrdersBySerial]);

  const loadHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      // Load both production reports and reported work orders
      const [reportsResult, workOrdersResult] = await Promise.all([
        getProductionHistory(),
        getReportedWorkOrders()
      ]);

      if (reportsResult.success) {
        setHistoryData(reportsResult.reports);
      } else {
        showToast(reportsResult.message, 'error');
      }

      if (workOrdersResult.success) {
        setReportedWorkOrders(workOrdersResult.workOrders);
      }
    } catch (error) {
      showToast('Failed to load history', 'error');
    }
    setIsLoadingHistory(false);
  }, [showToast]);

  // Load history when switching to history view
  useEffect(() => {
    if (viewMode === 'history') {
      loadHistory();
    }
  }, [viewMode, loadHistory]);

  // Load production employees on mount
  useEffect(() => {
    const loadEmployees = async () => {
      const result = await fetchProductionEmployees();
      if (result.success) {
        setEmployees(result.employees);
      }
    };
    loadEmployees();
  }, []);

  // Load the shelf count switch on mount
  useEffect(() => {
    let cancelled = false;
    getShelfCountSetting().then(result => {
      if (!cancelled) setShelfCountEnabled(result.enabled);
    });
    return () => { cancelled = true; };
  }, []);

  // Load reject reason codes on mount. Rejects are reportable without one, so a failure
  // here just leaves the selector hidden rather than surfacing an error.
  useEffect(() => {
    const loadDefectCodes = async () => {
      const result = await fetchDefectCodes();
      if (result.success) {
        setDefectCodes(result.defectCodes);
      }
    };
    loadDefectCodes();
  }, []);

  // Save activity filter to localStorage when it changes
  useEffect(() => {
    writeSetting('productionActivityFilter', activityFilter);
  }, [activityFilter]);

  // Save SKU grouping preference to localStorage when it changes
  useEffect(() => {
    writeSetting('productionGroupBySku', String(groupBySku));
  }, [groupBySku]);

  useEffect(() => {
    writeSetting('productionGroupByColour', String(groupByColour));
  }, [groupByColour]);

  // Keep the "Updated N min ago" text current
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const loadSavedWorkOrders = async () => {
    setIsLoading(true);
    try {
      const result = await getSavedWorkOrders();
      if (result.success) {
        // Initialize quantities with REMAINING quantity (planned - already reported),
        // keeping any quantities the user typed for rows still outstanding
        applyWorkOrdersList(result.workOrders);
        // Set fetch time from the most recent fetched_at
        if (result.workOrders[0]?.fetchedAt) {
          setLastFetchTime(new Date(result.workOrders[0].fetchedAt));
        }
      }
    } catch (error) {
      console.error('Error loading saved work orders:', error);
    }
    setIsLoading(false);
    setIsInitialLoad(false);
  };

  const handleFetchWorkOrders = async () => {
    setIsLoading(true);
    try {
      const result = await fetchReleasedWorkOrders();
      if (result.success) {
        // A full refresh from Priority resets every "To Report" to what's outstanding
        touchedQuantitiesRef.current = new Set();
        applyWorkOrdersList(result.workOrders);
        setLastFetchTime(new Date());
        refreshStock();
        const flags = {};
        result.workOrders.forEach(wo => { flags[wo.serialName] = Boolean(wo.missingComponents); });
        setMissingComponents(flags);
        // Clear selections and the customer filter (say so, so it isn't lost silently)
        setSelectedItems(new Set());
        const hadCustomerFilter = customerFilter !== 'all';
        setCustomerFilter('all');
        showToast(`Loaded ${result.count} work orders${hadCustomerFilter ? ' - customer filter cleared' : ''}`, 'success');
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      showToast('Failed to fetch work orders', 'error');
    }
    setIsLoading(false);
  };

  // Switch activity tab. The customer filter is kept when that customer has work orders
  // in the new activity; otherwise it's cleared with a note rather than silently.
  const handleActivityChange = (activity) => {
    setActivityFilter(activity);
    if (customerFilter === 'all') return;
    const keepsCustomer = workOrders.some(wo =>
      (activity === 'all' || wo.processName === activity)
      && (wo.customerName || wo.customerCode) === customerFilter);
    if (!keepsCustomer) {
      setCustomerFilter('all');
      showToast(`Customer filter cleared - ${customerFilter} has no work orders here`, 'info');
    }
  };

  const clearFilters = () => {
    setActivityFilter('all');
    setCustomerFilter('all');
    setSearchQuery('');
  };

  const handleSelectItem = (serialName) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serialName)) {
        newSet.delete(serialName);
      } else {
        newSet.add(serialName);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    // Use the fully filtered (activity + customer + search) work orders
    const filteredSerialNames = filteredWorkOrders.map(wo => wo.serialName);
    const allFilteredSelected = filteredSerialNames.every(sn => selectedItems.has(sn));

    if (allFilteredSelected && filteredWorkOrders.length > 0) {
      // Deselect all in current filter
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        filteredSerialNames.forEach(sn => newSet.delete(sn));
        return newSet;
      });
    } else {
      // Select all in current filter
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        filteredSerialNames.forEach(sn => newSet.add(sn));
        return newSet;
      });
    }
  };

  const handleQuantityChange = (serialName, value) => {
    // Allow empty value for clearing the field
    if (value === '') {
      touchedQuantitiesRef.current.add(serialName);
      setQuantities(prev => ({
        ...prev,
        [serialName]: ''
      }));
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      touchedQuantitiesRef.current.add(serialName);
      setQuantities(prev => ({
        ...prev,
        [serialName]: numValue
      }));
    }
  };

  const handleRejectQuantityChange = (serialName, value) => {
    if (value === '') {
      setRejectQuantities(prev => ({
        ...prev,
        [serialName]: ''
      }));
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setRejectQuantities(prev => ({
        ...prev,
        [serialName]: numValue
      }));
    }
  };

  const handleDefectCodeChange = (serialName, code) => {
    setDefectCodeSelections(prev => ({
      ...prev,
      [serialName]: code
    }));
  };

  // Selected work orders that still exist, in selection order
  const selectedWorkOrders = Array.from(selectedItems)
    .map(serialName => workOrdersBySerial.get(serialName))
    .filter(Boolean);

  // One shelf count per frame / mount part being reported
  const shelfCountParts = shelfCountEnabled
    ? Object.values(selectedWorkOrders.filter(asksShelfCount).reduce((acc, wo) => {
      if (!wo.partName) return acc;
      const entry = acc[wo.partName] || {
        partName: wo.partName,
        description: wo.partDescription || wo.partName,
        serialNames: []
      };
      entry.serialNames.push(wo.serialName);
      acc[wo.partName] = entry;
      return acc;
    }, {}))
    : [];

  // Identifies exactly what is about to be submitted (work orders and quantities)
  const batchSignature = () => selectedWorkOrders
    .map(wo => wo.serialName)
    .sort()
    .map(s => `${s}:${quantities[s] || 0}:${rejectQuantities[s] || 0}`)
    .join('|');

  const handleSubmitClick = () => {
    if (selectedWorkOrders.length === 0) {
      showToast('Please select at least one work order', 'warning');
      return;
    }
    // Check that all selected items have valid quantities (good qty or rejects)
    for (const workOrder of selectedWorkOrders) {
      const serialName = workOrder.serialName;
      const qty = quantities[serialName] || 0;
      const rejectQty = rejectQuantities[serialName] || 0;
      if (qty <= 0 && rejectQty <= 0) {
        showToast(`Work order ${serialName} needs a quantity or reject quantity`, 'warning');
        return;
      }
      // Check that all selected items have activity names
      if (!workOrder.activityName) {
        showToast(`Work order ${serialName} is missing activity name`, 'warning');
        return;
      }
      // Priority ERP rejects reported quantities exceeding 10x the work order quantity
      if (exceedsReportLimit(workOrder, qty, rejectQty)) {
        showToast(`Work order ${serialName}: total quantity (${qty + rejectQty}) exceeds 10x the planned quantity (${workOrder.quantity}). Priority ERP will reject this.`, 'error');
        return;
      }
    }

    // Mint a batch key, or keep the existing one when the selection and quantities are
    // byte-for-byte the same as the attempt whose outcome we never learned. Reusing a key
    // across a *different* batch would suppress a genuine report, so the signature must match.
    const signature = batchSignature();
    setPendingBatch(prev => (prev && prev.signature === signature ? prev : { key: makeBatchKey(), signature }));

    setShelfRecount({});
    setShowConfirmModal(true);
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
    setShelfRecount({});
  };

  const handleConfirmSubmit = async (shelfCounts = []) => {
    setIsSubmitting(true);

    // Shelf count first: the correction is worked out against Priority's stock BEFORE this
    // report adds to it. Every count is posted, however big the difference (the server
    // no longer asks for a recount; the needsRecount handling below is kept as a fallback).
    // If the check itself fails, production is still reported - it must never be blocked
    // by the stock check - and the result says the stock wasn't corrected.
    let shelfCount = null;
    if (shelfCounts.length > 0) {
      const employeeForCount = employees.find(e => String(e.userId) === String(selectedEmployee))?.name || undefined;
      const countResult = await submitShelfCount(shelfCounts, employeeForCount);
      if (countResult.needsRecount.length > 0) {
        setShelfRecount(Object.fromEntries(countResult.needsRecount.map(r => [r.partName, r.counted])));
        setIsSubmitting(false);
        return;
      }
      shelfCount = countResult.message
        ? { error: countResult.message, results: [] }
        : { error: null, results: countResult.results };
    }
    setShelfRecount({});
    setShowConfirmModal(false);

    // Only work orders that still exist are submitted (a selected one can disappear if
    // another user fully reports it first and realtime removes it).
    const submittedSerialNames = selectedWorkOrders.map(wo => wo.serialName);
    ownSubmissionRef.current = { serials: new Set(submittedSerialNames), until: Infinity };

    try {
      // Build items array - include plannedQuantity for edge function to use.
      const employeeName = employees.find(e => String(e.userId) === String(selectedEmployee))?.name || undefined;
      const items = selectedWorkOrders.map(workOrder => {
        const serialName = workOrder.serialName;
        const rejectQty = rejectQuantities[serialName] || 0;
        return {
          serialName,
          activityName: workOrder.activityName,
          quantity: quantities[serialName] || 0,
          rejectQuantity: rejectQty > 0 ? rejectQty : undefined,
          // Reason code only travels with an actual reject
          defectCode: rejectQty > 0 ? (defectCodeSelections[serialName] || undefined) : undefined,
          plannedQuantity: workOrder.quantity,  // Original planned qty from Priority
          partName: workOrder.partName,
          partDescription: workOrder.partDescription,
          salesOrder: workOrder.salesOrder,
          userId: selectedEmployee || undefined,
          employeeName
        };
      });

      if (items.length === 0) {
        setIsSubmitting(false);
        showToast('The selected work orders are no longer available. Please refresh and try again.', 'warning');
        setSelectedItems(new Set());
        return;
      }

      // Realtime updates can change untouched quantities while the dialog is open. A kept
      // key must only be reused for the exact batch it was minted for, or the server would
      // replay the earlier attempt's result instead of reporting these quantities.
      const signature = batchSignature();
      let batch = pendingBatch;
      if (!batch || batch.signature !== signature) {
        batch = { key: makeBatchKey(), signature };
        setPendingBatch(batch);
      }

      const result = await submitProductionReport(items, batch.key);

      // The same batch is still being processed by an earlier attempt. Keep the key and
      // the selection so a retry (once that attempt finishes) finds its report.
      if (result.isConflict) {
        showToast(shelfCount?.results.some(r => r.status === 'corrected')
          ? `${result.message} (the shelf count was saved)`
          : result.message, 'error');
        ownSubmissionRef.current = { ...ownSubmissionRef.current, until: Date.now() + OWN_ECHO_MS };
        setIsSubmitting(false);
        return;
      }

      // Warnings shown in the result dialog until it's closed:
      //   - production succeeded but raw materials weren't issued in Priority
      //   - Priority has the report but our copy of these work orders wasn't updated, so
      //     the list may show stale quantities until the next refresh
      const warnings = [
        result.materialWarning,
        result.workOrderUpdateFailures?.length > 0
          && `Reported, but ${result.workOrderUpdateFailures.length} work order(s) could not be updated on this screen (${result.workOrderUpdateFailures.join(', ')}). Fetch work orders to refresh.`
      ].filter(Boolean);

      // Per-line outcome with what was sent. lineResults are index-aligned with items.
      const lineFor = (item, line) => ({
        serialName: item.serialName,
        partName: item.partName,
        description: item.partDescription || item.partName || '-',
        good: Number(item.quantity) || 0,
        reject: Number(item.rejectQuantity) || 0,
        al: line?.al || null
      });
      const lineResults = result.lineResults || [];
      const reportedLines = [];
      const failedLines = [];
      items.forEach((item, i) => {
        const line = lineResults[i]?.serialName === item.serialName
          ? lineResults[i]
          : lineResults.find(r => r.serialName === item.serialName);
        if (!line) return;
        if (line.success) reportedLines.push(lineFor(item, line));
        else failedLines.push({ ...lineFor(item, line), ...describeLineFailure(line) });
      });
      const resultBase = {
        shelfCount,
        reportNo: result.reportNo || null,
        message: result.message,
        isNetworkError: Boolean(result.isNetworkError),
        duplicate: Boolean(result.duplicate),
        reported: reportedLines,
        failed: failedLines,
        warnings,
        // Labels are printed for the reported lines only
        printItems: items.filter(item => reportedLines.some(line => line.serialName === item.serialName)),
        lineResults
      };

      // Hold the key only while the outcome is genuinely unknown (no response). Any
      // definite answer - success or a rejection Priority actually returned - retires it,
      // so the next batch always gets a fresh key.
      if (!result.isNetworkError) {
        setPendingBatch(null);
      }

      // Reconcile the server-returned per-line quantities into local state. Shared by the
      // full-success and partial-failure paths so succeeded lines are always reflected and
      // their reject quantities are cleared (stale rejects must not be re-submitted).
      const applyUpdatedQuantities = (updatedQuantities) => {
        setWorkOrders(prevWorkOrders =>
          prevWorkOrders
            .map(wo => {
              const updated = updatedQuantities[wo.serialName];
              return updated
                ? {
                    ...wo,
                    reportedQuantity: updated.reportedQuantity,
                    remainingQuantity: updated.remainingQuantity,
                    status: updated.status
                  }
                : wo;
            })
            .filter(wo => wo.status !== 'reported')
        );
        // Reported rows go back to following the remaining quantity
        Object.keys(updatedQuantities).forEach(serialName => touchedQuantitiesRef.current.delete(serialName));
        setQuantities(prev => {
          const next = { ...prev };
          Object.entries(updatedQuantities).forEach(([serialName, data]) => {
            if (data.status !== 'reported') next[serialName] = remainingFor(data);
            else delete next[serialName];
          });
          return next;
        });
        setRejectQuantities(prev => {
          const next = { ...prev };
          Object.keys(updatedQuantities).forEach(serialName => { delete next[serialName]; });
          return next;
        });
        setDefectCodeSelections(prev => {
          const next = { ...prev };
          Object.keys(updatedQuantities).forEach(serialName => { delete next[serialName]; });
          return next;
        });
      };

      if (result.success) {
        // A full success with no line results (older function) still reported every item
        setReportResult({
          ...resultBase,
          kind: 'success',
          reported: reportedLines.length > 0 || result.duplicate ? reportedLines : items.map(item => lineFor(item, null)),
          printItems: reportedLines.length > 0 || result.duplicate ? resultBase.printItems : items
        });

        // Clear selections
        setSelectedItems(new Set());

        // Update local state with returned quantities (more reliable than DB reload)
        if (result.updatedQuantities) {
          applyUpdatedQuantities(result.updatedQuantities);
        } else {
          // Fallback: clear submitted rejects, then reload from database
          setRejectQuantities(prev => {
            const next = { ...prev };
            submittedSerialNames.forEach(s => { delete next[s]; });
            return next;
          });
          setDefectCodeSelections(prev => {
            const next = { ...prev };
            submittedSerialNames.forEach(s => { delete next[s]; });
            return next;
          });
          submittedSerialNames.forEach(s => touchedQuantitiesRef.current.delete(s));
          await loadSavedWorkOrders();
        }
      } else {
        // Partial failure (HTTP 207): some lines succeeded in Priority. Reconcile those so
        // they aren't re-reported, and deselect them — keep only the failed lines selected
        // so the user can retry just those.
        if (result.updatedQuantities) {
          applyUpdatedQuantities(result.updatedQuantities);
        }
        const succeeded = (result.lineResults || [])
          .filter(r => r.success)
          .map(r => r.serialName);
        if (succeeded.length > 0) {
          setSelectedItems(prev => {
            const next = new Set(prev);
            succeeded.forEach(s => next.delete(s));
            return next;
          });
        }
        setReportResult({
          ...resultBase,
          kind: result.reportNo && succeeded.length > 0 ? 'partial' : 'failed'
        });
      }
    } catch (error) {
      setReportResult({
        kind: 'failed',
        shelfCount,
        message: error.message || 'Failed to submit production report',
        isNetworkError: true,
        reportNo: null,
        reported: [],
        failed: [],
        warnings: [],
        printItems: [],
        lineResults: []
      });
    }

    ownSubmissionRef.current = { ...ownSubmissionRef.current, until: Date.now() + OWN_ECHO_MS };
    setIsSubmitting(false);
    // Reporting moves stock, so the figures beside the rows are now out of date
    refreshStock();
  };

  const handleCloseResult = () => setReportResult(null);

  // The failed lines are still selected: re-run the checks and reopen the confirm dialog
  const handleRetryFailed = () => {
    setReportResult(null);
    handleSubmitClick();
  };

  // Calculate summary stats
  const selectedCount = selectedWorkOrders.length;
  const totalQuantity = selectedWorkOrders.reduce(
    (sum, wo) => sum + (quantities[wo.serialName] || 0),
    0
  );

  const handleEmployeeChange = (userId) => {
    const value = userId ? Number(userId) : '';
    setSelectedEmployee(value);
    writeSetting('productionEmployeeId', value ? String(value) : null);
  };

  // Print labels for production report items. `date` is printed on the labels: the
  // report's own date for reprints from History, today otherwise.
  const printLabels = (items, lineResults, date = null) => {
    setPendingPrintData({ items, lineResults, date });
  };

  const handlePrintLabels = ({ layout, copies, serialNames }) => {
    const data = pendingPrintData;
    setPendingPrintData(null);
    if (!data) return;
    writeSetting('productionLabelLayout', layout);
    const items = data.items.filter(item => serialNames.has(item.serialName));

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow pop-ups to print labels.', 'error');
      return;
    }
    printWindow.document.write(buildLabelsDocument(items, data.lineResults, layout, { copies, date: data.date }));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  // Print the work orders shown (or only the selected ones) as a separate A4 sheet,
  // each colour on its own page. Selected work orders print in on-screen order; any
  // selected but hidden by the current filters follow at the end.
  const printWorkOrders = (selectedOnly) => {
    const list = selectedOnly
      ? [
          ...filteredWorkOrders.filter(wo => selectedItems.has(wo.serialName)),
          ...selectedWorkOrders.filter(wo => !filteredWorkOrders.includes(wo))
        ]
      : filteredWorkOrders;
    if (list.length === 0) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow pop-ups to print work orders.', 'error');
      return;
    }
    const context = [
      selectedOnly && `${list.length} selected`,
      !selectedOnly && customerFilter !== 'all' && customerFilter,
      !selectedOnly && searchTerm && `search "${searchQuery.trim()}"`
    ].filter(Boolean).join(' · ');
    printWindow.document.write(buildWorkOrdersDocument(list, { title: summaryTitle, context, byColour: groupByColour }));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const printCuttingList = (boards) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow pop-ups to print the cutting list.', 'error');
      return;
    }
    const context = [
      `${filteredWorkOrders.length} work order${filteredWorkOrders.length !== 1 ? 's' : ''}`,
      customerFilter !== 'all' && customerFilter,
      searchTerm && `search "${searchQuery.trim()}"`
    ].filter(Boolean).join(' · ');
    printWindow.document.write(buildCuttingListDocument(boards, context));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  // Rows for the confirm dialog: every work order that will be submitted, the ones
  // hidden by a filter or search first
  const visibleSerials = new Set(filteredWorkOrders.map(wo => wo.serialName));
  const hiddenSelectedCount = selectedWorkOrders.filter(wo => !visibleSerials.has(wo.serialName)).length;
  const confirmRows = showConfirmModal
    ? selectedWorkOrders
      .map(wo => {
        const serialName = wo.serialName;
        const good = Number(quantities[serialName]) || 0;
        const reject = Number(rejectQuantities[serialName]) || 0;
        const reasonCode = reject > 0 ? defectCodeSelections[serialName] || '' : '';
        const reason = defectCodes.find(dc => dc.code === reasonCode);
        return {
          serialName,
          description: wo.partDescription || wo.partName || '-',
          partName: wo.partName,
          good,
          reject,
          reasonCode,
          reasonLabel: reason ? `${reason.code}${reason.description ? ` — ${reason.description}` : ''}` : '',
          remaining: remainingFor(wo),
          overBy: overPlanBy(wo, good),
          needsConfirm: needsOverPlanConfirm(wo, good),
          hidden: !visibleSerials.has(serialName)
        };
      })
      .sort((a, b) => Number(b.hidden) - Number(a.hidden))
    : [];

  // Filters currently narrowing the list, shown as removable chips
  const activeFilters = [
    effectiveActivity !== 'all' && {
      key: 'activity',
      label: ROUTING_DISPLAY_NAMES[effectiveActivity] || effectiveActivity,
      clear: () => handleActivityChange('all')
    },
    customerFilter !== 'all' && { key: 'customer', label: customerFilter, clear: () => setCustomerFilter('all') },
    searchTerm && { key: 'search', label: `"${searchQuery.trim()}"`, clear: () => setSearchQuery('') }
  ].filter(Boolean);
  // The selected activity tab already shows the activity filter, so it gets no chip
  const chipFilters = activeFilters.filter(filter => filter.key !== 'activity');

  const allFilteredSelected = filteredWorkOrders.length > 0 && filteredWorkOrders.every(wo => selectedItems.has(wo.serialName));

  const rowViewProps = {
    displayRows,
    selectedItems,
    quantities,
    rejectQuantities,
    defectCodes,
    defectCodeSelections,
    isShort,
    partBalances: shelfCountEnabled ? null : partBalances,
    stockFetchedAt,
    hideCustomerCol,
    hideDateCol,
    changedSerials,
    onSelectItem: handleSelectItem,
    onSelectGroup: handleSelectGroup,
    onQuantityChange: handleQuantityChange,
    onRejectQuantityChange: handleRejectQuantityChange,
    onDefectCodeChange: handleDefectCodeChange
  };

  const submitDisabledReason = selectedCount === 0 ? 'Select work orders to report' : null;

  return (
    <div className="report-production-container">
      <Toast {...toastProps} />

      {/* Header with view toggle */}
      <div className="report-production-header">
        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'workOrders' ? 'active' : ''}`}
            onClick={() => setViewMode('workOrders')}
            aria-pressed={viewMode === 'workOrders'}
          >
            <ClipboardList size={14} /> Work Orders
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'history' ? 'active' : ''}`}
            onClick={() => setViewMode('history')}
            aria-pressed={viewMode === 'history'}
          >
            <History size={14} /> History
          </button>
        </div>

        {viewMode === 'workOrders' && (
          <div className="work-orders-header-buttons">
            {lastFetchTime && (
              <span
                className={`fetch-age ${isStale ? 'fetch-age--stale' : ''}`}
                role={isStale ? 'status' : undefined}
                title={isStale ? 'Work orders may be out of date - fetch them from Priority' : undefined}
              >
                {isStale && <AlertTriangle size={14} aria-hidden="true" />}
                Updated{' '}
                <time dateTime={lastFetchTime.toISOString()} title={lastFetchTime.toLocaleString('en-GB')}>
                  {formatAge(lastFetchTime, now)}
                </time>
              </span>
            )}
            {selectedCount > 0 && (
              <button
                className="print-work-orders-btn"
                onClick={() => printWorkOrders(true)}
                title="Print only the selected work orders"
              >
                <Printer size={14} /> Print selected ({selectedCount})
              </button>
            )}
            <button
              className="print-work-orders-btn"
              onClick={() => printWorkOrders(false)}
              disabled={filteredWorkOrders.length === 0}
              title="Print the work orders shown"
            >
              <Printer size={14} /> {selectedCount > 0 ? 'Print all shown' : 'Print'}
            </button>
            <button
              className={`fetch-work-orders-btn ${isStale ? 'fetch-work-orders-btn--stale' : ''}`}
              onClick={handleFetchWorkOrders}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : <><RefreshCw size={14} /> Fetch Work Orders</>}
            </button>
          </div>
        )}
      </div>

      {/* Work Orders View */}
      {viewMode === 'workOrders' && (
        <div className="work-orders-view">
          {/* Activity tabs + progress summary for what's shown (respects filters/search), one strip */}
          {workOrders.length > 0 && (
            <div className="wo-overview">
              {activityTypes.length > 1 && (
                <div className="activity-filter-tabs" role="group" aria-label="Activity">
                  <button
                    className={`activity-tab ${effectiveActivity === 'all' ? 'active' : ''}`}
                    onClick={() => handleActivityChange('all')}
                    aria-pressed={effectiveActivity === 'all'}
                  >
                    All ({sumItems(workOrders)})
                  </button>
                  {activityTypes.map(actName => (
                    <button
                      key={actName}
                      className={`activity-tab ${effectiveActivity === actName ? 'active' : ''}`}
                      onClick={() => handleActivityChange(actName)}
                      aria-pressed={effectiveActivity === actName}
                    >
                      {ROUTING_DISPLAY_NAMES[actName] || actName} ({activityCounts.get(actName) || 0})
                    </button>
                  ))}
                </div>
              )}
              {/* Title on its own when there are no tabs; always on the printed sheet */}
              <h2 className={`summary-title ${activityTypes.length > 1 ? 'print-only' : ''}`}>{summaryTitle}</h2>
              <div className="work-orders-summary">
                <p className="summary-remaining">
                  <span className="summary-remaining-value">{summaryRemaining.toLocaleString()}</span>
                  <span className="summary-remaining-label"> left of {summaryPlanned.toLocaleString()}</span>
                </p>
                <Meter value={summaryReported} max={summaryPlanned} label={`${summaryPct}% reported`} />
                <p className="summary-detail">
                  {summaryPct}% done &middot; {filteredWorkOrders.length} WO{filteredWorkOrders.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Customer Filter & Grouping Controls */}
          {workOrders.length > 0 && (
            <div className="filter-controls-row">
              {/* Phones: Select all lives here instead of in the action bar */}
              <label className="select-all-label select-all-filter">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAll}
                  disabled={filteredWorkOrders.length === 0}
                />
                Select all
              </label>
              <div className="work-order-search">
                <Search size={14} aria-hidden="true" />
                <input
                  type="search"
                  className="work-order-search-input"
                  placeholder="Search part, SKU, WO, order..."
                  aria-label="Search work orders"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="search-clear-btn" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    <X size={14} />
                  </button>
                )}
              </div>
              {(customerNames.length > 1 || customerFilter !== 'all') && (
                <div className="customer-filter">
                  <Filter size={14} aria-hidden="true" />
                  <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="customer-filter-select"
                    aria-label="Customer"
                  >
                    <option value="all">All Customers ({sumItems(activityFiltered)})</option>
                    {customerFilter !== 'all' && !customerNames.includes(customerFilter) && (
                      <option value={customerFilter}>{customerFilter} (0)</option>
                    )}
                    {customerNames.map(name => (
                      <option key={name} value={name}>
                        {name} ({customerCounts.get(name) || 0})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                className={`group-sku-toggle ${groupBySku ? 'active' : ''}`}
                onClick={() => setGroupBySku(prev => !prev)}
                aria-pressed={groupBySku}
              >
                <Layers size={14} /> Group by SKU
              </button>
              <button
                className={`group-sku-toggle ${groupByColour ? 'active' : ''}`}
                onClick={() => setGroupByColour(prev => !prev)}
                aria-pressed={groupByColour}
              >
                <Palette size={14} /> Group by colour
              </button>

              {/* Shared details (shown once when every row has the same value) + shelf count */}
              {filteredWorkOrders.length > 0 && (hideCustomerCol || hideDateCol || (shelfCountEnabled && stockPartsInView.length > 0)) && (
                <div className="shared-context">
                  {hideCustomerCol && (
                    <div className="shared-context-item">
                      <span className="shared-context-key">Customer</span>
                      <span className="shared-context-value">
                        {sharedWo.customerName || sharedWo.customerCode || '-'}
                        {sharedWo.salesOrder && <span className="sales-order">{sharedWo.salesOrder}</span>}
                      </span>
                    </div>
                  )}
                  {hideDateCol && (
                    <div className="shared-context-item">
                      <span className="shared-context-key">Due</span>
                      <span className="shared-context-value">
                        <DueDate date={sharedWo.endDate || sharedWo.plannedDate} inline />
                      </span>
                    </div>
                  )}
                  {shelfCountEnabled && stockPartsInView.length > 0 && (
                    <span
                      className="shelf-count-pill"
                      title="When you report frames or mounts you'll be asked how many are on the shelf, and Priority's stock is corrected to match."
                    >
                      <Info size={13} aria-hidden="true" /> Shelf count on
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Active filters as removable chips */}
          {workOrders.length > 0 && (chipFilters.length > 0 || hiddenSelectedCount > 0) && (
            <div className="filter-chips" aria-label="Active filters">
              {chipFilters.map(filter => (
                <button
                  key={filter.key}
                  type="button"
                  className="filter-chip"
                  onClick={filter.clear}
                  aria-label={`Remove filter ${filter.label}`}
                >
                  {filter.label} <X size={14} aria-hidden="true" />
                </button>
              ))}
              {chipFilters.length > 1 && (
                <button type="button" className="link-btn" onClick={clearFilters}>Clear filters</button>
              )}
              {hiddenSelectedCount > 0 && (
                <span className="hidden-selected-hint">
                  {hiddenSelectedCount} selected not shown
                </span>
              )}
            </div>
          )}

          {isLoading && isInitialLoad ? (
            <div className="skeleton-table">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="skeleton-table-row">
                  <div className="skeleton-table-cell skeleton" style={{ width: '40px' }} />
                  <div className="skeleton-table-cell skeleton" style={{ flex: 2 }} />
                  <div className="skeleton-table-cell skeleton" style={{ flex: 3 }} />
                  <div className="skeleton-table-cell skeleton" style={{ flex: 2 }} />
                  <div className="skeleton-table-cell skeleton" style={{ width: '60px' }} />
                  <div className="skeleton-table-cell skeleton" style={{ width: '60px' }} />
                </div>
              ))}
            </div>
          ) : workOrders.length === 0 ? (
            <div className="empty-state">
              <p>No pending work orders.</p>
              <p>Click "Fetch Work Orders" to load released work orders from Priority.</p>
            </div>
          ) : activityFiltered.length === 0 ? (
            <div className="empty-state">
              <p>No work orders for this activity.</p>
            </div>
          ) : filteredWorkOrders.length === 0 ? (
            <div className="empty-state">
              <p>No work orders match your filters.</p>
              <button className="clear-search-link" onClick={clearFilters}>Clear filters</button>
            </div>
          ) : (
            <>
              {/* MDF cutting list - Photo Blocks tab only */}
              {effectiveActivity === 'Blocks' && (
                <CuttingList
                  entries={filteredWorkOrders.map(wo => ({ wo, quantity: remainingFor(wo) }))}
                  onPrint={printCuttingList}
                />
              )}

              {stockParts.length > 0 && (
                <div className={`stock-note ${stockFailed ? 'stock-note--failed' : ''}`} role="status">
                  <span className="stock-note-text">
                    {stockFailed
                      ? "Couldn't load stock from Priority."
                      : <>
                          <strong>Priority stock</strong> is shown on each line. If the shelf doesn't match, earlier production was counted wrong - count carefully and report what was really made.
                          {stockFetchedAt && (
                            <span className="stock-note-time"> Updated {stockFetchedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.</span>
                          )}
                        </>}
                  </span>
                  <button type="button" className="stock-note-refresh" onClick={refreshStock} disabled={stockLoading}>
                    <RefreshCw size={13} className={stockLoading ? 'spinning' : ''} />
                    {stockFailed ? 'Retry' : 'Refresh stock'}
                  </button>
                </div>
              )}

              {/* Work Orders Table */}
              <WorkOrdersTable
                {...rowViewProps}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />

              {/* Work Orders Cards - tablet/mobile layout, swapped in via CSS at 900px and below */}
              <WorkOrdersCards {...rowViewProps} />

              {/* Action bar - pinned to the bottom of the screen so Submit stays in reach.
                  One compact row on phones: count + Submit. */}
              <div className="production-action-bar">
                <label className="select-all-label select-all-bar">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={handleSelectAll}
                  />
                  Select all
                </label>
                <span className="selected-count" aria-live="polite">
                  {selectedCount === 0
                    ? 'Tap work orders to select them'
                    : (
                      <>
                        <strong>{selectedCount}</strong> selected
                        {hiddenSelectedCount > 0 && <span className="selected-hidden"> ({hiddenSelectedCount} not shown)</span>}
                        <span className="selected-total">, <strong>{totalQuantity.toLocaleString()}</strong> to report</span>
                      </>
                    )}
                </span>
                <button
                  className="submit-production-btn"
                  onClick={handleSubmitClick}
                  disabled={selectedCount === 0 || isSubmitting}
                  title={submitDisabledReason || undefined}
                >
                  <Send size={14} />
                  <span className="submit-label-long">Submit production report</span>
                  <span className="submit-label-short">Submit</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <ProductionHistory
          historyViewType={historyViewType}
          setHistoryViewType={setHistoryViewType}
          isLoadingHistory={isLoadingHistory}
          historyData={historyData}
          reportedWorkOrders={reportedWorkOrders}
          selectedHistoryItem={selectedHistoryItem}
          setSelectedHistoryItem={setSelectedHistoryItem}
          onPrintLabels={printLabels}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmReportModal
          employees={employees}
          selectedEmployee={selectedEmployee}
          onEmployeeChange={handleEmployeeChange}
          rows={confirmRows}
          shelfCountParts={shelfCountParts}
          recount={shelfRecount}
          isSubmitting={isSubmitting}
          onCancel={handleCancelSubmit}
          onConfirm={handleConfirmSubmit}
        />
      )}

      {/* Submitting - blocks the page until Priority answers */}
      <Modal open={isSubmitting} dismissible={false} ariaLabel="Submitting to Priority" size="sm" className="production-modal">
        <div className="production-submitting" role="status">
          <div className="production-spinner" aria-hidden="true"></div>
          <p>Submitting to Priority ERP...</p>
          <p className="production-submitting-sub">Please wait</p>
        </div>
      </Modal>

      {/* Outcome of the submission */}
      {reportResult && (
        <ReportResultModal
          result={reportResult}
          onPrintLabels={() => printLabels(reportResult.printItems, reportResult.lineResults)}
          onRetryFailed={handleRetryFailed}
          onClose={handleCloseResult}
        />
      )}

      {/* Label options - opened last so it sits above the dialog that asked for it */}
      {pendingPrintData && (
        <LabelPickerModal
          items={pendingPrintData.items}
          initialLayout={readSetting('productionLabelLayout')}
          onPrint={handlePrintLabels}
          onCancel={() => setPendingPrintData(null)}
        />
      )}
    </div>
  );
};

export default ReportProduction;
